/* eslint-disable react/jsx-no-useless-fragment */
import {
  Flex,
  Box,
  VizFooter,
  ModalContainer,
  Button,
} from '@ouster/webviz-react-components';

import { useEffect } from 'react';
import { RxArrowRight } from 'react-icons/rx';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';

import SideNav from '../components/SideNav';
import {
  FlexColumnWithNav,
  NavFullHeightContainer,
  NavFlexRow,
} from '../containers/NavBar';
import { useKeycloak } from '../KeycloakContext';
import styles from '../pages/PublicPage.module.css';
import { useStoreActions, useStoreState } from '../reducers';

const PublicUploadWarning = () => (
  <Box display="grid" maxW={640} rowGap={12}>
    <Box lineHeight={1.8}>
      Sign up for a free Ouster Studio account to securely upload, visualize,
      and manage lidar recordings in the cloud. Get 250GB of free storage to
      start bringing data to life and collaborate seamlessly with a team.
    </Box>
    <Box
      display="flex"
      gap={4}
      mb={6}
      justifyContent="flex-end"
      alignItems="center"
    >
      <Link to="/login">
        <Button variant="link">Already a member? Log in</Button>
      </Link>
      <Link
        to="/sign_up"
        style={{ textDecoration: 'none', justifySelf: 'end' }}
      >
        <Button size="lg" minWidth={150}>
          Sign up for free
        </Button>
      </Link>
    </Box>
  </Box>
);

const PublicLayout = () => {
  const { keycloak } = useKeycloak();
  const location = useLocation();
  const navigate = useNavigate();
  const { isPublicWarningOpen } = useStoreState(
    (storeState) => storeState.recordingUpload
  );
  const { setIsPublicWarningOpen } = useStoreActions(
    (actions) => actions.recordingUpload
  );

  useEffect(() => {
    if (!keycloak || keycloak.authenticated) {
      return;
    }

    const fullPath = `${location.pathname}${location.search}`;
    const probeStorageKey = `kc-probe:${fullPath}`;

    if (sessionStorage.getItem(probeStorageKey) === 'done') {
      return;
    }

    sessionStorage.setItem(probeStorageKey, 'done');
    navigate(`/login?mode=probe&returnTo=${encodeURIComponent(fullPath)}`, {
      replace: true,
    });
  }, [keycloak, location.pathname, location.search, navigate]);

  return (
    <>
      <FlexColumnWithNav
        navRightEl={
          <NavFullHeightContainer>
            <NavFlexRow gap="16px">
              <div
                className={styles['action-buttons']}
                style={{ marginRight: '-8px' }}
                data-testid="public-action-buttons"
              >
                <Link
                  reloadDocument
                  to="/login"
                  className={styles['secondary-button']}
                  data-testid="public-login-link"
                >
                  Log in
                </Link>
                <Link
                  to="/sign_up"
                  className={styles['primary-button']}
                  data-testid="public-signup-link"
                >
                  Sign up for free <RxArrowRight />
                </Link>
              </div>
            </NavFlexRow>
          </NavFullHeightContainer>
        }
      >
        <Box
          h="100%"
          display="grid"
          gridTemplateRows="1fr auto"
          data-testid="public-layout-main"
        >
          <Flex overflow="auto">
            <Box
              w="min-content"
              bg="var(--ouster-colors-background-surface-subtle)"
              borderRight="1px solid"
              borderColor="var(--color-unify-white-30)"
              py={4}
              position="relative"
            >
              <SideNav mode="public" />
            </Box>
            <Box
              flex={1}
              height="100%"
              overflowX="hidden"
              bg="var(--color-gray-11)"
              data-testid="public-content-area"
            >
              <Outlet />
            </Box>
          </Flex>
          <VizFooter
            mode="none"
            borderTop="1px solid"
            borderColor="var(--color-unify-white-10)"
            py="1em"
          />
        </Box>
      </FlexColumnWithNav>
      {isPublicWarningOpen && (
        <ModalContainer
          isOpen={isPublicWarningOpen}
          onClose={() => setIsPublicWarningOpen(false)}
          header="Upload and Manage Lidar Data"
          footer={null}
          content={<PublicUploadWarning />}
          data-testid="public-upload-warning-modal"
        />
      )}
    </>
  );
};

export default PublicLayout;
